/* import __COLOCATED_TEMPLATE__ from './main.hbs'; */
/* RESPONSIBLE TEAM: team-integration-apps */
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type Store from '@ember-data/store';
import type IntlService from 'embercom/services/intl';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { action } from '@ember/object';
import { type TaskGenerator } from 'ember-concurrency';
import type ShopifyIntegration from 'embercom/models/shopify/integration';
import type RouterService from '@ember/routing/router-service';
import { runTask, pollTask } from 'ember-lifeline';
import ENV from 'embercom/config/environment';

interface ShopifySettingsMainSignature {
  Args: {
    registerHooks?: (hooks: { afterUninstall: { perform: () => Promise<void> } }) => void;
    hideExtras?: boolean;
  };
}

export default class ShopifySettingsMain extends Component<ShopifySettingsMainSignature> {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @tracked shops: ShopifyIntegration[] = [];
  @tracked shopToDisconnect: ShopifyIntegration | null = null;
  @tracked showModal = false;

  constructor(owner: unknown, args: ShopifySettingsMainSignature['Args']) {
    super(owner, args);
    taskFor(this.loadShops).perform();
    this._handleShopAlreadyConnectedNotification();
    if (this.args.registerHooks) {
      this.args.registerHooks({ afterUninstall: taskFor(this.cleanupShopifyData) });
    }
  }

  get app() {
    return this.appService.app;
  }

  get isLoadingShops() {
    return taskFor(this.loadShops).isRunning;
  }

  get isRefundReasonsEnabled() {
    return this.app?.canUseFeature('tdf-shopify-per-item-refund-reason') ?? false;
  }

  get isCustomerImportEnabled(): boolean {
    return this.app?.canUseFeature('shopify-bulk-user-import') ?? false;
  }

  get isOrderImportEnabled(): boolean {
    return this.app?.canUseFeature('shopify-bulk-order-import') ?? false;
  }

  get isImportStatusEnabled(): boolean {
    return this.isCustomerImportEnabled || this.isOrderImportEnabled;
  }

  get tableColumns(): { label: string; valuePath: string; type?: string }[] {
    let columns: { label: string; valuePath: string; type?: string }[] = [
      {
        label: this.intl.t('appstore.settings.shopify.column-headers.store-url'),
        valuePath: 'shopifyDomain',
      },
      {
        label: this.intl.t('appstore.settings.shopify.column-headers.connection-status'),
        valuePath: 'installationStatus',
        type: 'stamp',
      },
    ];
    if (this.isImportStatusEnabled) {
      columns.push({
        label: this.intl.t('appstore.settings.shopify.column-headers.import-status'),
        valuePath: 'importStatus',
      });
    }
    columns.push({ label: '', valuePath: 'reconnect' }, { label: '', valuePath: 'disconnect' });
    return columns;
  }

  get hasActiveImport(): boolean {
    return this.shops.some((shop) => shop.hasActiveImport);
  }

  @dropTask
  *loadShops(): TaskGenerator<void> {
    try {
      let records = (yield this.store.findAll(
        'shopify/integration',
      )) as unknown as ShopifyIntegration[];
      this.shops = records.slice();
      this.startPollingIfNeeded();
    } catch (error: unknown) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('appstore.settings.shopify.connect-shopify.retrieve-shops-failure'),
      });
    }
  }

  private startPollingIfNeeded() {
    if (this.hasActiveImport) {
      pollTask(this, 'pollImportStatus');
    }
  }

  async pollImportStatus(next: () => void) {
    try {
      let records = (await this.store.findAll(
        'shopify/integration',
      )) as unknown as ShopifyIntegration[];
      this.shops = records.slice();
    } catch {
      // Silently fail on poll — the UI still shows the last known state
    }
    if (this.hasActiveImport) {
      runTask(this, next, ENV.APP._5000MS);
    }
  }

  @dropTask
  *disconnectShop(): TaskGenerator<void> {
    try {
      yield this.shopToDisconnect?.destroyRecord();
      this.shopToDisconnect = null;
      this.notificationsService.notifyConfirmation(
        this.intl.t('appstore.settings.shopify.disconnect-store.notification.success-message'),
      );
    } catch (error: unknown) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t(
          'appstore.settings.shopify.disconnect-store.notification.failure-message',
        ),
      });
    }
  }

  @action
  showDisconnectShopModal(shop: ShopifyIntegration) {
    this.showModal = true;
    this.shopToDisconnect = shop;
  }

  @action
  confirmDisconnectShop() {
    taskFor(this.disconnectShop).perform();
    this.showModal = false;
  }

  @dropTask
  *cleanupShopifyData(): TaskGenerator<void> {
    this.store.unloadAll('shopify/integration');
    this.shops = [];
    yield Promise.resolve();
  }

  private _handleShopAlreadyConnectedNotification() {
    let queryParams = this.router.currentRoute?.queryParams || {};
    let notifyKey: { [key: string]: string } = {
      shop_already_connected:
        'appstore.settings.shopify.connect-shopify.shop-already-connected-message',
      shop_reconnected: 'appstore.settings.shopify.connect-shopify.shop-reconnected',
    };
    let notifyMessage = queryParams.notify ? notifyKey[queryParams.notify] : null;
    if (notifyMessage) {
      this.notificationsService.notifyConfirmation(this.intl.t(notifyMessage));
      let currentRouteName = this.router.currentRouteName;
      let newQueryParams = { ...queryParams };
      delete newQueryParams.notify;
      this.router.replaceWith(currentRouteName, { queryParams: newQueryParams });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Appstore::AppPackage::Settings::Shopify::Main': typeof ShopifySettingsMain;
    'appstore/app-package/settings/shopify/main': typeof ShopifySettingsMain;
  }
}
